<template>
  <div class="pinwall__chat" ref="chat" :style="setStyle">
    <div v-if="showChat">
      <label class="label">Chat</label>
      <b-button
        title="Hide chat"
        @click="toggleChat"
        class="hide-button"
        size="is-medium"
        type="is-text"
        icon-left="close"
      ></b-button>
      <b-button
        :title="showMessages ? 'Show users' : 'Show messages'"
        @click="toggleContent"
        class="toggle-button"
        size="is-medium"
        type="is-text"
        :icon-left="showMessages ? 'account' : 'forum'"
      >
        <span class="count">
          {{ showMessages ? users.length : messages.length }}
        </span>
      </b-button>
      <ul
        ref="messageContainer"
        v-if="messages && messages.length > 0 && showMessages"
        :style="windowHeight < 650 ? `height: ${windowHeight / 2 - 30}px` : ''"
      >
        <li v-for="message in messages" :key="message.id">
          <div class="details">
            <UserImage
              :src="message.userImg"
              :username="message.username"
              :view="'chat'"
            />
            <div class="meta">
              <div class="info">
                <span class="name">{{ message.username }}</span>
                <span>
                  {{ `• ${getTime(message)}` }}
                </span>
              </div>
              <div class="text">
                <div v-html="`${message.text}`" v-linkified></div>
              </div>
            </div>
            <b-button
              v-if="message.editable"
              @click="remove(message)"
              class="delete-button"
              type="is-text"
              icon-left="close"
            ></b-button>
          </div>
        </li>
      </ul>
      <ul
        class="user-list"
        v-if="users && users.length > 0 && !showMessages"
        :style="windowHeight < 650 ? `height: ${windowHeight / 2 - 30}px` : ''"
      >
        <li v-for="user in users" :key="user.id">
          <div class="details">
            <UserImage
              :src="user.userImg"
              :username="user.name"
              :view="'chat'"
            />
            <div class="meta">
              <div class="name">
                <p>{{ user.name }}</p>
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div class="send-container">
        <TextEditor ref="textEditor" @update="textUpdated"></TextEditor>
        <b-button
          :disabled="!user || text === ''"
          @click="create"
          icon-left="send"
          :title="
            !user
              ? $t('buttons.sendMessageDisabled')
              : $t('buttons.sendMessage')
          "
          class="button is-success is-rounded action-button"
        >
          {{ $t("send") }}
        </b-button>
        <b-button
          :title="$t('buttons.closeChatTitle')"
          @click="toggleChat"
          icon-left="close"
          class="button is-danger is-rounded action-button close-button"
          >{{ $t("close") }}</b-button
        >
      </div>
    </div>
    <div v-if="!showChat">
      <div class="show-button" title="Show chat" @click="toggleChat">
        <b-icon icon="forum" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { generateId, getTimeAgoString } from "../../utils/helpers";
import TextEditor from "../core/TextEditor";
import UserImage from "../core/UserImage.vue";

export default {
  name: "Chat",
  data: () => {
    return {
      text: "",
      showChat: true,
      showMessages: true,
      users: [],
      chatUser: null,
      messages: [],
      clientHeight: 0
    };
  },
  props: {
    pinwallId: {
      type: String
    },
    chatOptions: {
      type: Object,
      default: null
    },
    subscribeId: {
      type: String,
      default: "415d2973-592f-4a5e-8851-e401b40ddf85"
    }
  },
  components: {
    TextEditor,
    UserImage
  },
  computed: {
    ...mapGetters(["user"]),
    setStyle() {
      if (!this.showChat) {
        document
          .getElementById("pinwallHtml")
          .setAttribute("style", "overflow-y:auto !important");
        return "background: transparent";
      } else {
        if (this.windowWidth <= 700 || this.windowHeight <= 700) {
          document
            .getElementById("pinwallHtml")
            .setAttribute("style", "overflow-y:hidden !important");
        } else {
          document
            .getElementById("pinwallHtml")
            .setAttribute("style", "overflow-y:auto !important");
        }
        return "";
      }
    }
  },
  methods: {
    ...mapActions(["createMessage", "deleteMessage", "loadChat"]),
    textUpdated(value) {
      this.text = value;
    },
    toggleContent() {
      this.showMessages = !this.showMessages;
      this.scrollDown();
    },
    toggleChat() {
      this.showChat = !this.showChat;
      this.scrollDown();
    },
    getTime(item) {
      return getTimeAgoString(item.createDate, this.$t("time"));
    },
    gotDataFromServer(dataString) {
      let data = JSON.parse(dataString);
      const action = data.action;
      if (!action) {
        return;
      }

      if (action === "new-message") {
        data.createDate = data.date;

        let messageExist = this.messages.find(msg => {
          return msg.id === data.id;
        });

        if (!messageExist) {
          this.messages.push(data);
          this.scrollDown();
        }
      }

      if (action === "delete-message") {
        this.messages = this.messages.filter(msg => {
          return msg.id !== data.id;
        });
      }

      if (action === "new-user") {
        let userExist = this.users.find(user => {
          return user.id === data.id;
        });

        if (!userExist) {
          this.users.push(data);

          this.chatUser.action = "check-user";
          this.$socket.emit(
            process.env.VUE_APP_SOCKET_ON,
            this.subscribeId,
            JSON.stringify(this.chatUser)
          );
        }
      }

      if (action === "check-user") {
        let userExist = this.users.find(user => {
          return user.id === data.id;
        });

        if (!userExist) {
          this.users.push(data);
        }
      }

      if (action === "delete-user") {
        this.users = this.users.filter(user => {
          return user.id !== data.id;
        });
      }
    },
    scrollDown() {
      const messageContainer = this.$refs.messageContainer;
      if (this.showMessages && this.showChat && messageContainer) {
        this.$nextTick(() => {
          messageContainer.scrollTop = messageContainer.scrollHeight;
        });
      }
    },
    create() {
      const newMessage = {
        id: generateId(),
        pinwallId: this.pinwallId,
        text: this.text,
        action: "new-message",
        username: this.user ? this.user.username : this.chatOptions.name,
        userImg: this.user ? this.user.photo : null,
        date: new Date()
      };
      this.createMessage(newMessage).then(() => {
        this.$refs.textEditor.editor.clearContent();
        this.text = "";
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          JSON.stringify(newMessage)
        );
        this.scrollDown();
      });
    },
    remove(message) {
      this.deleteMessage(message).then(() => {
        this.messages = this.messages.filter(msg => {
          return message.id !== msg.id;
        });
        message.action = "delete-message";
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          JSON.stringify(message)
        );
      });
    }
  },
  created() {
    if (
      this.windowWidth <= process.env.VUE_APP_MOBILE_WIDTH ||
      this.windowHeight <= process.env.VUE_APP_MOBILE_WIDTH
    ) {
      this.showChat = false;
    }

    this.sockets.subscribe(this.subscribeId, data => {
      this.gotDataFromServer(data);
    });

    this.chatUser = {
      name: this.user ? this.user.username : this.chatOptions.name,
      userImg: this.user ? this.user.photo : null,
      id: this.user ? this.user.id : generateId(),
      action: "new-user"
    };

    this.users.push(this.chatUser);

    this.$socket.emit(
      process.env.VUE_APP_SOCKET_ON,
      this.subscribeId,
      JSON.stringify(this.chatUser)
    );

    this.loadChat(this.pinwallId).then(messages => {
      this.messages = messages;
      this.scrollDown();
    });
  },
  mounted() {
    this.$nextTick(() => {
      this.clientHeight = this.$refs.chat.clientHeight;
    });
  },
  beforeDestroy() {
    document
      .getElementById("pinwallHtml")
      .setAttribute("style", "overflow-y:auto !important");
    this.sockets.unsubscribe(this.subscribeId);
    this.chatUser.action = "delete-user";
    this.$socket.emit(
      process.env.VUE_APP_SOCKET_ON,
      this.subscribeId,
      JSON.stringify(this.chatUser)
    );
  }
};
</script>
